import { Button, Text } from '@/components/ui';
import { Table } from '@/components/ui/Table';
import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { User } from '@console/utils';
import { RoleBadge } from './components/RoleBadge';
import { Switch } from '@/components/ui/Switch';
import { Actions } from './components/Actions';
import { Loader } from '@/components/Loader';
import { useState, useMemo } from 'react';
import { useUsers } from '@/hooks/useUsers';
import { CreateUserModal } from './components/CreateUserModal';
import { UpdateUserModal } from './components/UpdateUserModal';
import { Pagination } from '@/components/ui/Pagination';

export function UsersPage() {
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { t } = useTranslation();
  const { users, pagination, isLoading, isSaving, createUser, updateUser, deleteUser, refetch } =
    useUsers({
      page: 1,
      limit: 10,
    });

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUpdateModalOpen(true);
  };

  const confirmDeleteUser = async (userId: string) => {
    const success = await deleteUser(userId);
    if (success) {
      setSelectedUser(null);
    }
  };

  const handlePageChange = (page: number) => {
    refetch({ page, limit: pagination.limit });
  };

  const columns: ColumnDef<User>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('users.table.name'),
      },
      {
        accessorKey: 'email',
        header: t('users.table.email'),
      },
      {
        header: t('users.table.role'),
        cell: ({ row: { original } }) => <RoleBadge role={original.role} />,
      },
      {
        header: t('users.table.group'),
        cell: ({ row: { original } }) => original.group?.name || '-',
      },
      {
        header: t('users.table.active'),
        cell: ({ row: { original } }) => (
          <Switch
            checked={original.isActive}
            onCheckedChange={(checked) => updateUser(original.id, { isActive: checked })}
            disabled={isSaving}
          />
        ),
      },
      {
        id: 'actions',
        cell: ({ row: { original } }) => (
          <Actions
            user={original}
            onEdit={handleEditUser}
            onDelete={() => confirmDeleteUser(original.id)}
            isLoading={isSaving}
          />
        ),
      },
    ],
    [updateUser, isSaving, handleEditUser, confirmDeleteUser, t]
  );

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <Loader isLoading={isLoading} />
      <CreateUserModal
        open={newModalOpen}
        onOpenChange={setNewModalOpen}
        onCreate={createUser}
        isCreating={isSaving}
      />
      {selectedUser && (
        <UpdateUserModal
          open={updateModalOpen}
          onOpenChange={(open) => {
            setUpdateModalOpen(open);
            if (!open) setSelectedUser(null);
          }}
          user={selectedUser}
          onUpdate={updateUser}
          isUpdating={isSaving}
        />
      )}

      <header className="flex w-full items-center justify-between">
        <Text as="h1" variant="heading">
          {t('users.title')}
        </Text>
        <div>
          <Button onClick={() => setNewModalOpen(true)} disabled={isLoading}>
            <PlusIcon size={20} />
            {t('common.create')}
          </Button>
        </div>
      </header>

      <Table data={users} columns={columns} />
      <Pagination
        className="mt-lg"
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
        disabled={isLoading}
      />
    </div>
  );
}
